import { colors } from "../theme";

interface FeaturesProps {
  isDark: boolean;
}

export function Features({ isDark }: FeaturesProps) {
  const coreFeatures = [
    {
      title: "Decreasing costs",
      desc: "Storage gets cheaper the longer data ages. Your 3-year-old backups shouldn't cost the same as yesterday's.",
      color: isDark ? colors.cyan : colors.blue,
      icon: "↓",
    },
    {
      title: "Smart retrieval",
      desc: "Search by meaning, not just keywords. We index content, recognize images, and transcribe audio so you can find what you need without remembering exact filenames.",
      color: colors.magenta,
      icon: "◎",
    },
    {
      title: "No egress traps",
      desc: "Predictable pricing when you need your data back. We're not in the business of holding your data hostage.",
      color: colors.yellow,
      icon: "↗",
    },
  ];

  const addons = [
    {
      name: "Extra Durability",
      desc: "Configurable enhanced redundancy for critical archives",
    },
    {
      name: "Buddy Backup",
      desc: "Disperse encrypted parity to a partner site for shared disaster recovery",
    },
    {
      name: "Priority Access",
      desc: "Faster wake times when you need data now",
    },
    {
      name: "Data Scrubs & Reports",
      desc: "Integrity verification and compliance reporting",
    },
    {
      name: "SSO & Multi-tenancy",
      desc: "Enterprise access controls and team management",
    },
    {
      name: "Private Inference",
      desc: "PII and HIPAA-compliant ML processing on your data, fully isolated",
    },
  ];

  return (
    <section id="features" className="py-32 px-8">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-semibold tracking-tight mb-6">
            Archive smarter, not harder
          </h2>
          <p
            className="text-xl max-w-2xl mx-auto"
            style={{ color: isDark ? colors.base01 : colors.base1 }}
          >
            Intelligent by default. Your data is indexed, classified, and
            searchable from the start—not because we bolted on AI, but because
            that's what good storage should do.
          </p>
        </div>

        {/* Core features */}
        <div className="grid md:grid-cols-3 gap-6 mb-12">
          {coreFeatures.map((item, i) => (
            <div
              key={i}
              className="p-8 rounded-2xl transition-all duration-300 hover:scale-105"
              style={{
                backgroundColor: `${item.color}08`,
                border: `1px solid ${item.color}30`,
              }}
            >
              <div
                className="w-10 h-10 rounded-lg flex items-center justify-center text-xl mb-4"
                style={{
                  backgroundColor: `${item.color}20`,
                  color: item.color,
                }}
              >
                {item.icon}
              </div>
              <h3 className="font-semibold text-lg mb-2">{item.title}</h3>
              <p style={{ color: isDark ? colors.base01 : colors.base1 }}>
                {item.desc}
              </p>
            </div>
          ))}
        </div>

        {/* Add-ons */}
        <div
          className="p-8 rounded-2xl transition-colors duration-[2000ms]"
          style={{
            backgroundColor: isDark ? colors.base02 : colors.base2,
            border: `1px solid ${isDark ? colors.base01 : colors.base1}20`,
          }}
        >
          <div className="flex items-center gap-2 mb-6">
            <span
              className="text-xs uppercase tracking-widest px-2 py-1 rounded-full"
              style={{
                backgroundColor: colors.magenta + "20",
                color: colors.magenta,
              }}
            >
              Coming soon
            </span>
            <h3 className="font-semibold text-lg">Feature roadmap</h3>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {addons.map((addon, i) => (
              <div key={i}>
                <h4 className="font-medium mb-1">{addon.name}</h4>
                <p
                  className="text-sm"
                  style={{ color: isDark ? colors.base01 : colors.base1 }}
                >
                  {addon.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
